import type { Currency } from '../enums/currency';
import PowerduckState from '../../app/powerduck-state';
import LanguageTranslator from '../enum-translation/language-translator';
import { Language } from '../enums/language';
import { sortBy } from '../extensions/array-extensions';

// eslint-disable-next-line ts/no-namespace
export namespace LanguageUtils {
	// eslint-disable-next-line import/no-mutable-exports, prefer-const
	export let supportedLanguages = [
		Language.sk,
		Language.en,
	];

	export interface LanguageListItem {
		text: string;
		value: string;
		flag: string;
		id: Language;
	}

	const getAdjectiveName = (name: string): string => {
		if (name.endsWith('ý')) {
			name = `${name.substring(0, name.length - 1)}y`;
		}

		return name;
	};

	export const getLanguageList = (sort?: boolean): Array<LanguageListItem> => {
		const retVal = <Array<LanguageListItem>>[];

		const getLanguage = (langCode: Language): LanguageListItem => {
			const newOption = <LanguageListItem>{
				id: langCode,
				text: getAdjectiveName(LanguageTranslator.getString(langCode)),
				value: langCode.toString(),
				flag: null,
			};

			switch (langCode) {
				case 'sk':
					newOption.flag = 'svk';
					break;
				case 'en':
					newOption.flag = 'gbr';
					break;
				case 'cs':
					newOption.flag = 'cze';
					break;
				case 'de':
					newOption.flag = 'ger';
					break;
				case 'pl':
					newOption.flag = 'pol';
					break;
				case 'it':
					newOption.flag = 'ita';
					break;
				case 'hu':
					newOption.flag = 'hun';
					break;
				default:
					break;
			}

			return newOption;
		};

		for (const langCode of [
			Language.sk,
			Language.en,
			Language.cs,
			Language,
			Language.pl,
			Language.it,
			Language.hu,
			Language.de,
		]) {
			if (supportedLanguages.includes(langCode as any)) {
				retVal.push(getLanguage(langCode as any));
			}
		}

		if (sort === true) {
			try {
				retVal.sort((a, b) => a.text.localeCompare(b.text));
			} catch (e) {
				retVal[sortBy](p => p.text);
			}
		}

		(LanguageUtils as any).getLanguageList = () => retVal;
		return retVal;
	};

	/**
	 * Returns enum value from string language code
	 *
	 * @param langCode 2-letter language code [sk, en, cs...]
	 */
	export const getLanguageEnum = (langCode: string): Language => (langCode as any) ?? Language.en;

	export const getLanguageFlagUrl = (languageFlag: string, svg?: boolean): string => {
		if (!svg) {
			return `${PowerduckState.getAssetRootDirectory()}/img/flags/${languageFlag}.png`;
		} else {
			return `${PowerduckState.getAssetRootDirectory()}/img/flags/svg/${languageFlag}.svg`;
		}
	};

	const currencyMap: Record<string, string> = {
		PLN: 'zł',
		CZK: 'kč',
		EUR: '€',
		HUF: 'Ft',
	};

	const currencyRegex = new RegExp(Object.keys(currencyMap).join('|'), 'g');

	const normalizeCurrency = (value: string): string => value.replace(currencyRegex, match => currencyMap[match]);
	export const floatToCurrency = (
		value: number,
		currency: string | Currency | number | any,
		killZeroCents?: boolean,
	): string => {
		if (value == null) {
			value = 0;
		}

		let currencyISO: string;
		if (currency == null || currency == '') {
			currencyISO = '?';
		} else {
			currencyISO = currency as any;
		}

		if (killZeroCents == null) {
			killZeroCents = true;
		}

		if (killZeroCents) {
			try {
				return normalizeCurrency((Math.round(value * 100) / 100).toLocaleString(PowerduckState.getCurrentLanguage(), {
					style: 'currency',
					currency: currencyISO,
				})).replace(' ,00', '').replace(' .00', '').replace(',00', '').replace('.00', '');
			} catch (e) {
				return (`${(Math.round(value * 100) / 100).toString()} ${currencyISO}`).replace(' ,00', '').replace(' .00', '').replace(',00', '').replace('.00', '');
			}
		} else {
			try {
				return normalizeCurrency((Math.round(value * 100) / 100).toLocaleString(PowerduckState.getCurrentLanguage(), {
					style: 'currency',
					currency: currencyISO,
				}));
			} catch (e) {
				return `${(Math.round(value * 100) / 100).toString()} ${currencyISO}`;
			}
		}
	};

	export const negateString = (str: string): string => PowerduckState.getResourceValue('negationBase') + str.toLowerCase();
}
